import { type TypedDocumentNode, gql } from '@apollo/client';
import classNames from 'classnames';
import { FormattedMessage, defineMessages, useIntl } from 'react-intl';
import styled from 'styled-components';

import {
  So5FixtureEvent,
  Sport,
} from '@sorare/core/src/__generated__/globalTypes';
import { Button } from '@sorare/core/src/atoms/buttons/Button';
import { IconButton } from '@sorare/core/src/atoms/buttons/IconButton';
import { Ball } from '@sorare/core/src/atoms/icons/Ball';
import { MLBBall } from '@sorare/core/src/atoms/icons/MLBBall';
import { NBABall } from '@sorare/core/src/atoms/icons/NBABall';
import { Horizontal } from '@sorare/core/src/atoms/layout/flex';
import { Badge } from '@sorare/core/src/atoms/ui/Badge';
import {
  ANY_SPORT_PLAY_EVENTTYPE_FIXTURE,
  ANY_SPORT_PLAY_EVENTTYPE_FIXTURE_ENTER,
  ANY_SPORT_PLAY_EVENTTYPE_FIXTURE_MATCH_CENTER,
} from '@sorare/core/src/constants/routes';
import { useSportContext } from '@sorare/core/src/contexts/sport';
import { useIsLaptop } from '@sorare/core/src/hooks/device/useIsLaptop';
import { fantasy } from '@sorare/core/src/lib/glossary';

import {
  getLeaguesWithContenders,
  isFixtureClosed,
  isFixtureLive,
  isFixtureOpened,
} from 'lib/so5';

import { DiscoverCompetitionsButton } from '../DiscoverCompetitionsButton';
import type {
  RightButton_ongoingSo5Fixture,
  RightButton_so5Fixture,
} from './__generated__/index.graphql';

const StyledIconButton = styled(IconButton)`
  &.live {
    border: 2px solid var(--c-red-600);
  }
`;
const Wrapper = styled.div`
  position: relative;
  isolation: isolate;
`;
const Tag = styled.div`
  z-index: 1;
  background: var(--c-red-600);
  border-radius: var(--half-unit);
  padding: 2px var(--half-unit);
  position: absolute;
  left: 50%;
  transform: translate(-50%, -50%);
  font: 10px / 10px var(--sorareFont);
  font-weight: var(--t-bold);
`;

const messages = defineMessages({
  viewMatches: {
    id: 'play.pro.$fixture.layout.viewMatches',
    defaultMessage: 'View Matches',
  },
  viewGames: {
    id: 'play.pro.$fixture.layout.viewGames',
    defaultMessage: 'View Games',
  },
});

type Props = {
  so5Fixture: RightButton_so5Fixture;
  sport: Sport;
  showDiscoverNewCompetitions: boolean;
  ongoingSo5Fixture?: Nullable<RightButton_ongoingSo5Fixture>;
};
export const RightButton = ({
  so5Fixture,
  sport,
  showDiscoverNewCompetitions,
  ongoingSo5Fixture,
}: Props) => {
  const isLive = isFixtureLive(so5Fixture);
  const isPast = isFixtureClosed(so5Fixture);
  const isUpcoming = isFixtureOpened(so5Fixture);
  const isLaptopAndAbove = useIsLaptop();
  const { generateSportContextPath } = useSportContext();
  const { formatMessage } = useIntl();

  if (isPast && ongoingSo5Fixture) {
    return (
      <Button
        color="tertiary"
        size={isLaptopAndAbove ? 'medium' : 'large'}
        to={generateSportContextPath(ANY_SPORT_PLAY_EVENTTYPE_FIXTURE, {
          params: {
            eventType: ongoingSo5Fixture.type.toLowerCase(),
            fixture: ongoingSo5Fixture.slug,
          },
        })}
      >
        <FormattedMessage
          id="play.pro.$fixture.layout.backToToday"
          defaultMessage="Back to Today"
        />
      </Button>
    );
  }

  const { so5LeaderboardGroupsWithContenders } = getLeaguesWithContenders(
    so5Fixture.so5LeaderboardGroups,
    isUpcoming
  );

  if (!so5LeaderboardGroupsWithContenders.length) {
    return null;
  }

  const matchCenterUrl = generateSportContextPath(
    ANY_SPORT_PLAY_EVENTTYPE_FIXTURE_MATCH_CENTER,
    {
      params: {
        eventType: so5Fixture.type.toLowerCase(),
        fixture: so5Fixture.slug,
      },
    }
  );
  const discoverCompetitionsUrl = generateSportContextPath(
    ANY_SPORT_PLAY_EVENTTYPE_FIXTURE_ENTER,
    {
      params: {
        eventType: so5Fixture.type.toLowerCase(),
        fixture: so5Fixture.slug,
      },
    }
  );

  if (isLive) {
    const viewMatchesMessage = formatMessage(messages.viewMatches);
    const viewGamesMessage = formatMessage(messages.viewGames);
    if (
      (so5Fixture.type === So5FixtureEvent.DAILY || so5Fixture.canCompose) &&
      showDiscoverNewCompetitions
    ) {
      const { myLivePlayerGameScoresCount } = so5Fixture;
      const withLivePlayers = myLivePlayerGameScoresCount > 0;

      return (
        <Horizontal>
          <DiscoverCompetitionsButton
            discoverCompetitionsUrl={discoverCompetitionsUrl}
            sport={sport}
            ongoingFixtureStartDate={ongoingSo5Fixture?.startDate}
          />
          <Badge variant="dot" invisible={!withLivePlayers} overlap="circular">
            <Wrapper>
              <StyledIconButton
                color="secondary"
                to={matchCenterUrl}
                className={classNames({ live: withLivePlayers })}
              >
                {sport === Sport.FOOTBALL && (
                  <Ball title={viewMatchesMessage} />
                )}
                {sport === Sport.NBA && <NBABall title={viewGamesMessage} />}
                {sport === Sport.BASEBALL && (
                  <MLBBall title={viewGamesMessage} />
                )}
              </StyledIconButton>
              {withLivePlayers && (
                <Tag>{formatMessage(fantasy.live).toUpperCase()}</Tag>
              )}
            </Wrapper>
          </Badge>
        </Horizontal>
      );
    }
    return (
      <Button
        color="secondary"
        size={isLaptopAndAbove ? 'medium' : 'large'}
        to={matchCenterUrl}
      >
        {sport === Sport.FOOTBALL ? viewMatchesMessage : viewGamesMessage}
      </Button>
    );
  }

  if (isUpcoming && showDiscoverNewCompetitions) {
    return (
      <DiscoverCompetitionsButton
        discoverCompetitionsUrl={discoverCompetitionsUrl}
        sport={sport}
        ongoingFixtureStartDate={ongoingSo5Fixture?.startDate}
      />
    );
  }

  return null;
};

RightButton.fragments = {
  so5Fixture: gql`
    fragment RightButton_so5Fixture on So5Fixture {
      slug
      type
      canCompose
      myLivePlayerGameScoresCount
      so5LeaderboardGroups {
        slug
        ...getLeaguesWithContenders_so5LeaderboardGroupInterface
      }
      ...isFixtureLive_so5Fixture
      ...isFixtureClosed_so5Fixture
      ...isFixtureOpened_so5Fixture
    }
    ${isFixtureLive.fragments.so5Fixture}
    ${isFixtureClosed.fragments.so5Fixture}
    ${isFixtureOpened.fragments.so5Fixture}
    ${getLeaguesWithContenders.fragments.so5LeadearboardGroupInterface}
  ` as TypedDocumentNode<RightButton_so5Fixture>,
  ongoingSo5Fixture: gql`
    fragment RightButton_ongoingSo5Fixture on So5Fixture {
      slug
      type
      startDate
    }
  ` as TypedDocumentNode<RightButton_ongoingSo5Fixture>,
};
